import React, { useState, useRef, useEffect } from 'react';
import { Send, Zap, Mic, ArrowLeft, AlertTriangle, TrendingUp, TrendingDown, DollarSign, Wallet, Clock, Paperclip, ArrowRight, RotateCcw, Ban } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { CASHFLOW_DATA, CASHFLOW_CHAT_RESPONSES } from '../../data/cashflowData';

const MetricCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = item.alert ? (isDark ? 'bg-red-950/15' : 'bg-red-50/40') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const cardBorder = item.alert ? (isDark ? 'border-red-500/15' : 'border-red-200/50') : border;
  return (
    <div className={`${cardBg} border ${cardBorder} rounded-xl px-3 py-2.5 text-center`}>
      <p className={`text-[9px] ${ts}`}>{item.label}</p>
      <p className={`text-base font-bold mt-0.5 ${item.alert ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>{item.value}</p>
      <span className={`text-[9px] font-medium ${item.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>{item.trend}</span>
    </div>
  );
};

const InsightCard = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const icons = { recommendation: Zap, alert: AlertTriangle, insight: TrendingUp };
  const colors = { recommendation: isDark ? 'text-indigo-400' : 'text-indigo-600', alert: isDark ? 'text-red-400' : 'text-red-500', insight: isDark ? 'text-emerald-400' : 'text-emerald-600' };
  const Icon = icons[item.type] || TrendingUp;
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]'}`}>
      <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${colors[item.type]}`} />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}>{item.text}</p>
        <p className={`text-[9px] mt-1 ${ts}`}>Finn · {item.time}</p>
      </div>
    </div>
  );
};

const V4FinanceCashflow = ({ isDark, onWorkflowToggle, workflowPanelOpen }) => {
  const [message, setMessage] = useState('');
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const messagesEndRef = useRef(null);

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages]);

  const sendMessage = (text, resp) => {
    setChatMessages(prev => [...prev, { role: 'user', text }, { role: 'agent', text: resp?.text || CASHFLOW_CHAT_RESPONSES.general.text }]);
    setChatMode(true);
  };
  const handleSend = () => { if (message.trim()) { sendMessage(message.trim()); setMessage(''); } };
  const handleCta = (label, detail) => {
    sendMessage(`${label} — ${detail}`, { text: `I'll handle "${label}" for ${detail}. Setting that up now and will confirm once ready.\n\nAnything else you need?` });
  };

  const maxWaterfall = Math.max(...CASHFLOW_DATA.waterfall.map(w => Math.abs(w.amount)));

  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Finn about cashflow..." rows={1} className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`} style={{ height: 'auto', maxHeight: '200px' }} data-testid="cashflow-chat-input" />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.finn} alt="" className="w-5 h-5 rounded-full object-cover" /><span className={`text-xs font-medium ${tp}`}>Finn</span>
              </div>
              <button onClick={onWorkflowToggle} className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}>
                <Zap className={`w-3.5 h-3.5 ${tp}`} /><span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
              <button className={`p-1.5 rounded-lg ${hoverBg}`}><Paperclip className={`w-3.5 h-3.5 ${ts}`} /></button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center ${hoverBg}`}><Mic className={`w-4 h-4 ${ts}`} /></button>
              <button onClick={handleSend} className={`w-8 h-8 rounded-xl flex items-center justify-center ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`} data-testid="cashflow-send-btn">
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="cashflow-chat-view">
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button onClick={() => setChatMode(false)} className={`flex items-center gap-2 px-2 py-1 rounded-lg ${hoverBg}`} data-testid="cashflow-back-btn">
            <ArrowLeft className={`w-4 h-4 ${ts}`} /><span className={`text-sm font-medium ${tp}`}>Back to Cashflow</span>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto"><div className="flex justify-center"><div className="w-[80%] py-5 space-y-5">
          {chatMessages.map((msg, i) => (
            <div key={i} className={msg.role === 'user' ? 'flex justify-end' : 'flex gap-3'}>
              {msg.role === 'agent' && <img src={AGENT_THUMBS.finn} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-1" />}
              <div className={msg.role === 'user' ? `max-w-[75%] px-3.5 py-2.5 rounded-2xl rounded-br-md text-sm leading-relaxed ${isDark ? 'bg-indigo-500/15 text-white' : 'bg-indigo-500/10 text-slate-800'}` : `text-sm leading-relaxed whitespace-pre-line ${tp}`}>{msg.text}</div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div></div></div>
        {chatInput}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="cashflow-page">
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wallet className={`w-5 h-5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Cashflow</p>
          </div>
          <button onClick={() => handleCta('Record Payment', 'Log an incoming or outgoing payment')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:opacity-90" data-testid="cashflow-record-btn">
            <DollarSign className="w-3.5 h-3.5" /> Record Payment
          </button>
        </div>
      </div>
      <div className={`border-t ${border}`} />

      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
        <div className="grid grid-cols-6 gap-3">
          {CASHFLOW_DATA.metrics.map((m, i) => <MetricCard key={i} item={m} isDark={isDark} />)}
        </div>

        {/* Waterfall chart */}
        <div className={`${cardBg} border ${border} rounded-xl p-4`}>
          <p className={`text-[11px] font-semibold mb-5 ${tp}`}>Monthly Cash Flow Breakdown</p>
          <div className="flex flex-col gap-[14px]">
            {CASHFLOW_DATA.waterfall.map(w => {
              const pct = (Math.abs(w.amount) / maxWaterfall) * 100;
              const isInflow = w.type === 'inflow';
              return (
                <div key={w.category} className="flex items-center gap-3">
                  <span className={`text-[10px] w-[110px] text-right flex-shrink-0 ${ts}`}>{w.category}</span>
                  <div className="flex-1 h-[7px] rounded-full overflow-hidden" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }}>
                    <div className={`h-full rounded-full ${isInflow ? (isDark ? 'bg-emerald-500/40' : 'bg-emerald-500/35') : (isDark ? 'bg-red-500/30' : 'bg-red-500/25')}`} style={{ width: `${pct}%` }} />
                  </div>
                  <span className={`text-[10px] w-14 text-right font-medium tabular-nums ${isInflow ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>
                    {isInflow ? '+' : '-'}${(Math.abs(w.amount) / 1000).toFixed(0)}K
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Two columns: Receivables + Payables */}
        <div className="grid grid-cols-2 gap-4">
          {/* Receivables */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <TrendingDown className={`w-4 h-4 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Receivables (Overdue)</p>
              <span className={`text-[9px] ${ts}`}>{CASHFLOW_DATA.receivables.filter(r => r.daysOverdue > 0).length} accounts</span>
            </div>
            <div className={`${cardBg} border ${border} rounded-xl overflow-hidden`}>
              <div className={`grid grid-cols-[2fr_1fr_1fr_1.5fr] items-center px-3 py-1.5 border-b ${border}`}>
                <span className={`text-[8px] font-semibold ${ts}`}>Client</span>
                <span className={`text-[8px] font-semibold text-center ${ts}`}>Amount</span>
                <span className={`text-[8px] font-semibold text-center ${ts}`}>Overdue</span>
                <span className={`text-[8px] font-semibold text-right ${ts}`}>Action</span>
              </div>
              {CASHFLOW_DATA.receivables.filter(r => r.daysOverdue > 0).map(r => {
                const isCritical = r.status === 'critical';
                const isOverdue = r.status === 'overdue';
                return (
                  <div key={r.id} className={`grid grid-cols-[2fr_1fr_1fr_1.5fr] items-center px-3 py-2 border-b ${border} last:border-0 ${isCritical ? (isDark ? 'bg-red-500/5' : 'bg-red-50/30') : ''}`} data-testid={`receivable-${r.id}`}>
                    <div>
                      <p className={`text-[10px] font-medium ${tp}`}>{r.client}</p>
                      <p className={`text-[8px] ${ts}`}>{r.invoice}</p>
                    </div>
                    <span className={`text-[10px] font-bold text-center ${isCritical ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>{r.amount}</span>
                    <span className={`text-[10px] text-center font-medium ${isCritical ? (isDark ? 'text-red-400' : 'text-red-600') : isOverdue ? (isDark ? 'text-amber-400' : 'text-amber-600') : ts}`}>{r.daysOverdue}d</span>
                    <div className="flex justify-end gap-1">
                      {isCritical ? (
                        <button onClick={() => handleCta('Escalate Collection', r.client)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap bg-amber-500/10 text-amber-400 hover:bg-amber-500/20`}>
                          <AlertTriangle className="w-3 h-3" /> Escalate
                        </button>
                      ) : (
                        <button onClick={() => handleCta('Send Reminder', r.client)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}>
                          <Send className="w-3 h-3" /> Remind
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Payables */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Upcoming Payables</p>
              <span className={`text-[9px] ${ts}`}>{CASHFLOW_DATA.payables.length} payments</span>
            </div>
            <div className={`${cardBg} border ${border} rounded-xl overflow-hidden`}>
              <div className={`grid grid-cols-[2fr_1fr_1fr_1.5fr] items-center px-3 py-1.5 border-b ${border}`}>
                <span className={`text-[8px] font-semibold ${ts}`}>Vendor</span>
                <span className={`text-[8px] font-semibold text-center ${ts}`}>Amount</span>
                <span className={`text-[8px] font-semibold text-center ${ts}`}>Due</span>
                <span className={`text-[8px] font-semibold text-right ${ts}`}>Action</span>
              </div>
              {CASHFLOW_DATA.payables.map(p => {
                const needsApproval = p.status === 'pending-approval';
                return (
                  <div key={p.id} className={`grid grid-cols-[2fr_1fr_1fr_1.5fr] items-center px-3 py-2 border-b ${border} last:border-0 ${needsApproval ? (isDark ? 'bg-amber-500/5' : 'bg-amber-50/30') : ''}`} data-testid={`payable-${p.id}`}>
                    <div>
                      <p className={`text-[10px] font-medium ${tp}`}>{p.vendor}</p>
                      <p className={`text-[8px] ${ts}`}>{p.category}{p.recurring ? ' · Recurring' : ''}</p>
                    </div>
                    <span className={`text-[10px] font-bold text-center ${tp}`}>{p.amount}</span>
                    <span className={`text-[10px] text-center ${ts}`}>{p.dueDate}</span>
                    <div className="flex justify-end gap-1">
                      {needsApproval ? (
                        <>
                          <button onClick={() => handleCta('Approve Payment', p.vendor)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'}`}>
                            Approve
                          </button>
                          <button onClick={() => handleCta('Defer Payment', p.vendor)} className={`flex items-center gap-1 px-2 py-1 rounded-md text-[9px] font-medium transition-colors whitespace-nowrap ${isDark ? 'bg-white/[0.04] text-white/60 hover:bg-white/[0.08]' : 'bg-black/[0.03] text-slate-500 hover:bg-black/[0.06]'}`}>
                            Defer
                          </button>
                        </>
                      ) : (
                        <span className={`text-[9px] px-2 py-0.5 rounded-full ${p.status === 'due' ? (isDark ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600') : (isDark ? 'bg-white/[0.04] text-white/40' : 'bg-black/[0.03] text-slate-500')}`}>
                          {p.status === 'due' ? 'Due Soon' : 'Scheduled'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Insights */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <img src={AGENT_THUMBS.finn} alt="" className="w-4 h-4 rounded-full object-cover" />
            <p className={`text-xs font-semibold ${tp}`}>Finn's Insights</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {CASHFLOW_DATA.insights.map(item => <InsightCard key={item.id} item={item} isDark={isDark} />)}
          </div>
        </div>
      </div>

      {chatInput}
    </div>
  );
};

export default V4FinanceCashflow;
